/** 通用的父子层级工具：按 `parentId` 建树、收集后代。 */

export type TreeItem = { id: string; parentId: string | null };

export type TreeNode<T> = { item: T; depth: number };

/**
 * 把带 `parentId` 的扁平列表组织成森林并做深度优先展开。
 * 父节点不在集合内（如被筛掉）的节点视作根，保证筛选后仍能完整展示。
 * @param compare 同级排序比较器，缺省不排序（保持传入顺序）。
 */
export function buildTree<T extends TreeItem>(items: T[], compare?: (a: T, b: T) => number): TreeNode<T>[] {
  const byParent = new Map<string | null, T[]>();
  const ids = new Set(items.map((item) => item.id));

  for (const item of items) {
    const key = item.parentId && ids.has(item.parentId) ? item.parentId : null;
    const siblings = byParent.get(key) ?? [];
    siblings.push(item);
    byParent.set(key, siblings);
  }

  if (compare) {
    for (const siblings of byParent.values()) {
      siblings.sort(compare);
    }
  }

  const result: TreeNode<T>[] = [];
  const walk = (parentId: string | null, depth: number) => {
    for (const item of byParent.get(parentId) ?? []) {
      result.push({ item, depth });
      walk(item.id, depth + 1);
    }
  };
  walk(null, 0);
  return result;
}

/** 收集某节点的全部后代 id（编辑时用于排除非法父级选项，避免成环）。 */
export function collectDescendantIds<T extends TreeItem>(rootId: string, items: T[]): Set<string> {
  const byParent = new Map<string, T[]>();
  for (const item of items) {
    if (item.parentId) {
      const siblings = byParent.get(item.parentId) ?? [];
      siblings.push(item);
      byParent.set(item.parentId, siblings);
    }
  }

  const descendants = new Set<string>();
  const stack = [rootId];
  while (stack.length > 0) {
    const current = stack.pop() as string;
    for (const child of byParent.get(current) ?? []) {
      if (descendants.add(child.id)) {
        stack.push(child.id);
      }
    }
  }
  return descendants;
}

/**
 * 按谓词筛选，同时把命中节点的全部祖先一起保留下来。
 * 配置页搜索常常只命中子节点，只留命中项会让结果失去层级上下文；
 * 补上祖先后 {@link buildTree} 仍能拼出一棵连贯的树。
 */
export function filterWithAncestors<T extends TreeItem>(items: T[], predicate: (item: T) => boolean): T[] {
  const byId = new Map(items.map((item) => [item.id, item]));
  const kept = new Set<string>();

  for (const item of items) {
    if (!predicate(item)) {
      continue;
    }
    kept.add(item.id);

    // 已在集合里的祖先说明其上游也补齐过了，可以提前收工；顺带也挡住了数据成环时的死循环。
    let parent = item.parentId ? byId.get(item.parentId) : undefined;
    while (parent && !kept.has(parent.id)) {
      kept.add(parent.id);
      parent = parent.parentId ? byId.get(parent.parentId) : undefined;
    }
  }

  return items.filter((item) => kept.has(item.id));
}
